import React from 'react';

import { LinkOutlined } from '@ant-design/icons';
import { useFormik } from 'formik';

import { useUpsertWebhookConfig } from '../api/upsertWebhookConfig';
import { buildWebhookConfigSchema } from '../schemas/webhookConfigSchema';
import { WEBHOOK_AUTH_TYPE } from '../types';
import { IWebhookFormValues, WebhookForm } from './WebhookForm';
import CancelSubmitFooterModal from '@/components/CancelSubmitFooterModal';
import CustomModal from '@/components/CustomModal';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface IAddWebhookModalProps {
  partnerId: string;
  open: boolean;
  staticConfig: string[];
  onClose: () => void;
  onSuccess?: (configId: string) => void;
}

export const AddWebhookModal = ({
  partnerId,
  open,
  staticConfig,
  onClose,
  onSuccess,
}: IAddWebhookModalProps) => {
  const addToast = useToasts((state) => state.addItem);

  const { mutateAsync: upsertWebhookConfig, isPending } =
    useUpsertWebhookConfig({ partnerId });

  const formik = useFormik<IWebhookFormValues>({
    initialValues: {
      url: '',
      authType: WEBHOOK_AUTH_TYPE.PUBLIC,
    },
    validationSchema: buildWebhookConfigSchema(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const payloads = staticConfig.reduce<Record<string, boolean>>(
          (acc, key) => ({ ...acc, [key]: false }),
          {}
        );
        const { authType: _authType, ...apiValues } = values;
        const newConfig = await upsertWebhookConfig({
          values: { ...apiValues, ...payloads },
        });
        addToast(
          { msg: 'Webhook URL created', type: MessageType.Success },
          'root',
          {}
        );
        formik.resetForm();
        onClose();

        onSuccess?.(newConfig.config_id);
      } catch (err) {
        addToast(
          {
            msg: err instanceof Error ? err.message : 'Create failed',
            type: MessageType.Error,
          },
          'root',
          {}
        );
      }
    },
  });

  const isInvalid = !formik.values.url || !!formik.errors.url;

  const handleCancel = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <CustomModal
      title={
        <span>
          <LinkOutlined /> Add Webhook
        </span>
      }
      open={open}
      destroyOnClose
      footer={CancelSubmitFooterModal(
        isInvalid,
        formik.handleSubmit,
        handleCancel,
        isPending
      )}
      onCancel={handleCancel}
      width="50%"
    >
      <WebhookForm formik={formik} />
    </CustomModal>
  );
};
